import { ListFilterState, SavedView } from './listFilterValues';

type Storage = {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<unknown>;
};
type Snapshot = { state: ListFilterState; views: SavedView[]; ready: boolean };

const isRecord = (value: unknown): value is Record<string, any> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const customFields = (value: unknown): Record<string, string[]> => isRecord(value)
  ? Object.fromEntries(Object.entries(value).filter(([, values]) => Array.isArray(values))
    .map(([key, values]) => [key, values.filter((v: unknown) => typeof v === 'string')]))
  : {};
const savedViewsFrom = (value: unknown): SavedView[] => Array.isArray(value) ? value
  .filter((v) => isRecord(v) && typeof v.id === 'string' && typeof v.name === 'string' && isRecord(v.filters))
  .map((v) => ({ id: v.id, name: v.name, filters: v.filters, customFields: customFields(v.customFields),
    sort: typeof v.sort === 'string' ? v.sort : undefined, search: typeof v.search === 'string' ? v.search : undefined })) : [];

export const emptyListFilterState = (sort?: string): ListFilterState => ({ filters: {}, customFields: {}, search: '', sort });

/** Each business/list owns its state and storage keys, including while reads are pending. */
export function createListFilterStore(storage: Storage, businessId: string, screen: string, defaultSort?: string, remember = true) {
  const keyFor = (kind: 'state' | 'views') => `listFilters:v1:${kind}:${businessId || 'none'}:${screen}`;
  let snapshot: Snapshot = { state: emptyListFilterState(defaultSort), views: [], ready: !remember };
  const listeners = new Set<() => void>();
  let stateRevision = 0;
  let viewsReady = false;
  let pendingViewUpdates: Array<(views: SavedView[]) => SavedView[]> = [];
  let writes = Promise.resolve();

  const publish = (next: Snapshot) => {
    snapshot = next;
    listeners.forEach((listener) => listener());
  };
  const read = async (kind: 'state' | 'views') => {
    try {
      const raw = await storage.getItem(keyFor(kind));
      return raw ? JSON.parse(raw) : undefined;
    } catch { return undefined; }
  };
  const write = (kind: 'state' | 'views', value: unknown) => {
    // Keep rapid edits in order; storage failures must not prevent filtering.
    const json = JSON.stringify(value);
    writes = writes.then(() => storage.setItem(keyFor(kind), json)).then(() => undefined, () => undefined);
  };
  const persistState = () => {
    if (!remember || !snapshot.ready) return;
    // Search is deliberately transient: an old search can silently hide records.
    const { search: _search, ...rest } = snapshot.state;
    write('state', rest);
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    restore: () => {
      if (snapshot.ready && viewsReady) return () => {};
      let cancelled = false;
      const initialStateRevision = stateRevision;
      void Promise.all([remember ? read('state') : undefined, read('views')]).then(([saved, savedViews]) => {
        if (cancelled) return;
        const stateChanged = stateRevision !== initialStateRevision;
        const restoredState: ListFilterState = isRecord(saved) ? {
          filters: isRecord(saved.filters) ? saved.filters : {},
          customFields: customFields(saved.customFields),
          sort: typeof saved.sort === 'string' ? saved.sort : defaultSort,
          view: typeof saved.view === 'string' ? saved.view : undefined,
          search: '',
        } : emptyListFilterState(defaultSort);
        // A saved-view edit made before loading must preserve previously saved views.
        const hadViewEdits = pendingViewUpdates.length > 0;
        const views = pendingViewUpdates.reduce((current, update) => update(current), savedViewsFrom(savedViews));
        pendingViewUpdates = [];
        viewsReady = true;
        publish({
          state: stateChanged || !remember ? snapshot.state : restoredState,
          views,
          ready: true,
        });
        if (stateChanged) persistState();
        if (hadViewEdits) write('views', views);
      });
      return () => { cancelled = true; };
    },
    updateState: (update: (state: ListFilterState) => ListFilterState) => {
      stateRevision++;
      publish({ ...snapshot, state: update(snapshot.state) });
      persistState();
    },
    updateViews: (update: (views: SavedView[]) => SavedView[]) => {
      if (!viewsReady) pendingViewUpdates.push(update);
      const views = update(snapshot.views);
      publish({ ...snapshot, views });
      if (viewsReady) write('views', views);
    },
  };
}
